'use client';

import { useState } from 'react';
import classnames from 'classnames';
import { Button } from '@components/v3/atoms/button';
import Dropdown from '@components/v3/molecules/dropdown';
import DateRangePicker from '@components/molecules/date-range-picker';
import type { DateRange } from 'react-day-picker';
import {
  FixturesSearchBarProps,
  FixturesSearchState,
  SearchOption,
} from './fixturesSearchBar.types';
import styles from './fixturesSearchBar.module.scss';

const defaultOnSearch = (state: FixturesSearchState) =>
  // eslint-disable-next-line no-console
  console.info('[FixturesSearchBar] search', state);

const FixturesSearchBar = ({
  competitions,
  clubs,
  competitionLabel = 'Competition',
  dateLabel = 'Date range',
  clubLabel = 'Club',
  submitLabel = 'Search',
  competitionPlaceholder = 'All competitions',
  clubPlaceholder = 'All clubs',
  datePlaceholder = 'Select dates',
  initialCompetition,
  initialDateRange,
  initialClub,
  onSearch = defaultOnSearch,
  onCompetitionChange,
  classNames = [],
  testId = 'fixtures-search-bar',
}: FixturesSearchBarProps) => {
  const [competition, setCompetition] = useState<SearchOption | undefined>(
    initialCompetition
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    initialDateRange
  );
  const [club, setClub] = useState<SearchOption | undefined>(initialClub);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Mocked isolation preview — no network. Emits the current selection only.
    onSearch({ competition, dateRange, club });
  };

  return (
    <form
      className={classnames(styles.fixturesSearchBar, classNames)}
      data-testid={testId}
      onSubmit={handleSubmit}
    >
      <div className={styles.fixturesSearchBar__content}>
        <div className={styles.fixturesSearchBar__fields}>
          <div className={styles.fixturesSearchBar__field}>
            <span className={styles.fixturesSearchBar__label}>
              {competitionLabel}
            </span>
            <div className={styles.fixturesSearchBar__control}>
              <Dropdown<SearchOption>
                id="fixtures-search-competition"
                transparent
                classNames={[classnames(styles.fixturesSearchBar__dropdown)]}
                defaultItem={
                  initialCompetition ?? { id: '', text: competitionPlaceholder }
                }
                items={competitions}
                onSelection={(item) => {
                  const next = item.id ? item : undefined;
                  setCompetition(next);
                  // Changing competition invalidates the club selection.
                  setClub(undefined);
                  onCompetitionChange?.(next);
                }}
              />
            </div>
          </div>

          <div className={styles.fixturesSearchBar__field}>
            <span className={styles.fixturesSearchBar__label}>{dateLabel}</span>
            <DateRangePicker
              placeholder={datePlaceholder}
              value={dateRange}
              onChange={setDateRange}
            />
          </div>

          <div className={styles.fixturesSearchBar__field}>
            <span className={styles.fixturesSearchBar__label}>{clubLabel}</span>
            <div className={styles.fixturesSearchBar__control}>
              <Dropdown<SearchOption>
                // Remount when the competition changes so the uncontrolled
                // dropdown clears its displayed selection.
                key={competition?.id ?? 'no-competition'}
                id="fixtures-search-club"
                transparent
                classNames={[classnames(styles.fixturesSearchBar__dropdown)]}
                defaultItem={initialClub ?? { id: '', text: clubPlaceholder }}
                items={clubs}
                onSelection={(item) => setClub(item.id ? item : undefined)}
              />
            </div>
          </div>
        </div>

        <div className={styles.fixturesSearchBar__buttons}>
          <Button
            text={submitLabel}
            variant="primary"
            nativeProps={{ type: 'submit' }}
          />
        </div>
      </div>
    </form>
  );
};

export default FixturesSearchBar;
